import cn from 'classnames';
import { ChangeEvent, FC, MouseEventHandler, useRef } from 'react';
import { DropzoneState } from 'react-dropzone';
import { useIntl } from 'react-intl';

import { useMedia } from '@/shared/hooks';
import { Icon } from '@/shared/ui';

import styles from './UploadDentalPhotosDropzone.module.scss';

type UploadDentalPhotosDropzoneProps = {
  dropzoneState: DropzoneState;
  canUploadFolder?: boolean;
  onCameraCapture?: (files: File[]) => void;
};

export const UploadDentalPhotosDropzone: FC<UploadDentalPhotosDropzoneProps> = (
  props,
) => {
  const {
    dropzoneState: { getRootProps, getInputProps, inputRef },
    canUploadFolder = true,
    onCameraCapture,
  } = props;

  const { formatMessage } = useIntl();
  const { isMobile } = useMedia();

  const cameraInputRef = useRef<HTMLInputElement>(null);

  const handleUploadFolder: MouseEventHandler<HTMLDivElement> = (event) => {
    event.stopPropagation();
    inputRef.current?.click();
  };

  const handleTakePhoto: MouseEventHandler<HTMLDivElement> = (event) => {
    event.stopPropagation();
    cameraInputRef.current?.click();
  };

  const handleCameraChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;

    if (files && files.length > 0) {
      onCameraCapture?.(Array.from(files));
    }

    event.target.value = '';
  };

  return (
    <div
      data-component="upload-dental-photos-dropzone"
      className={styles.container}
      {...getRootProps()}
    >
      <div className={styles.buttonsWrapper}>
        {isMobile && (
          <div className={styles.button} onClick={handleTakePhoto}>
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className={styles.hiddenInput}
              onChange={handleCameraChange}
            />

            <Icon name="camera" size={28} />

            <p className={cn(styles.title, 'p2')}>
              {formatMessage({
                id: 'dentalPhotos.upload.takePhoto',
                defaultMessage: 'Take photo',
              })}
            </p>
          </div>
        )}

        <div className={styles.button}>
          <input {...getInputProps()} />

          <Icon name="file" size={isMobile ? 28 : 24} />

          <p className={cn(styles.title, 'p2')}>
            {formatMessage({
              id: 'dropzone.uploadFile.title',
              defaultMessage: 'Upload files',
            })}
          </p>
        </div>

        {canUploadFolder && !isMobile && (
          <div className={styles.button} onClick={handleUploadFolder}>
            <input
              id="uploadFolderInput"
              {...getInputProps()}
              type="file"
              webkitdirectory=""
            />

            <Icon name="folder" size={28} />

            <p className={cn(styles.title, 'p2')}>
              {formatMessage({
                id: 'dropzone.uploadFolder.title',
                defaultMessage: 'Upload folder',
              })}
            </p>
          </div>
        )}
      </div>

      {!isMobile && (
        <p className={cn(styles.text, 'p3')}>
          {formatMessage({
            id: 'dropzone.dragAndDrop.text',
            defaultMessage: 'Or drag and drop file or folder here',
          })}
        </p>
      )}
    </div>
  );
};
